// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import { css } from "styled-components";

interface OwnProps {
    shipTo: ShipToModel;
    extendedStyles?: ShippingAddressInfoDisplayStyles;
    cartPageLink?: any;
    isQuote?: boolean;
}

export interface ShippingAddressInfoDisplayStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    address?: AddressInfoDisplayStyles;
    headingText?: TypographyPresentationProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    iconProps?: IconProps;
}

export const shippingAddressInfoDisplayStyles: ShippingAddressInfoDisplayStyles = {
    headingGridItem: {
        width: 12,
        css: css`
            align-items: flex-end;
            display: flex;
            flex-wrap: wrap;
            @media (max-width: 481px) {
                &&& {
                    margin-top: 20px;
                }
            }
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-right: 15px;
            white-space: nowrap;
        `,
    },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    container: {
        gap: 0,
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    editLink: {
        css: css`
            white-space: nowrap;
            margin-bottom: 10px;
            span {
                display: flex;
                align-items: center;
            }
        `,
    },
    iconProps: {
        src: "ArrowRight",
        color: "primary",
        size: 17,
        css: css`
            margin-left: 3px;
        `,
    },
    address: {
        companyNameText: {
            as: "p",
            weight: fontConfig.weight.semiBold,
            css: css`
                text-transform: lowercase;
                &:first-line {
                    text-transform: capitalize;
                }
                font-style: normal !important;
            `,
        },
    },
};

const ShippingAddressInfoDisplay = ({ shipTo, extendedStyles, cartPageLink, isQuote }: OwnProps) => {
    const styles = mergeToNew(shippingAddressInfoDisplayStyles, extendedStyles);
    return (
        <GridContainer {...styles.container} data-test-selector="shippingAddressInfoDisplay_container">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.headingText} as="h3">
                    {translate("Shipping Address")}
                </Typography>
                {!isQuote && (
                    <Link
                        {...styles.editLink}
                        href={cartPageLink}
                        data-test-selector="checkoutReviewAndSubmit_changeShipping"
                    >
                        {translate("Change Fulfillment Method to Pick Up")}
                        <Icon {...styles.iconProps}></Icon>
                    </Link>
                )}
            </GridItem>
            <GridItem {...styles.addressDisplayAndFormGridItem}>
                <AddressInfoDisplay
                    {...shipTo}
                    state={shipTo.state ? shipTo.state.abbreviation : undefined}
                    country={shipTo.country ? shipTo.country.abbreviation : undefined}
                    extendedStyles={styles.address}
                />
            </GridItem>
        </GridContainer>
    );
};

export default ShippingAddressInfoDisplay;
